import lodash from "lodash";
import { Control, ControlType } from "@/types/controls";
import { BuilderElement } from "@/types/elements";
import { GlobalColor } from "@/types/colors";
import { extractVariableName } from "@/lib/colors/colors";
import { configStore, globalStore } from "@/store/store-accessor";
import { TreeNode } from "@/types/tree";
import { ClipboardData } from "@/types/copy-paste";
import defensiveCopy from "@/util/defensive-copy";
import { TypographyPreset } from "@/types/typography";

function getPathsByType(type: ControlType, element: BuilderElement) {
  const addPathToAccumulator = (
    control: Control,
    parentPath: string,
    acc: string[]
  ) => {
    const curPath = `${parentPath}.${control.slug}`;

    if (type === control.options.type) {
      acc.push(curPath);
    }

    control.children.forEach(control => {
      acc = addPathToAccumulator(control, curPath, acc);
    });

    return acc;
  };

  const paths: string[] = [];

  element.controls.contentSections.forEach(control => {
    paths.concat(addPathToAccumulator(control, "content", paths));
  });

  element.controls.designSections.forEach(control => {
    paths.concat(addPathToAccumulator(control, "design", paths));
  });

  return paths;
}

function moveExternalSiteGlobalsIntoElement(
  node: TreeNode,
  copiedPresets: TypographyPreset[],
  copiedColors: GlobalColor[]
) {
  const element = defensiveCopy(node);
  const builderElement = configStore.element(element.data.type);

  /*
   * Typography Presets
   */
  const typographyPresets = getPathsByType("typography", builderElement);

  typographyPresets.forEach(path => {
    const presetId = lodash.get(element.data.properties, `${path}.preset`);

    if (!presetId) return;

    const foundCopiedPreset = maybeImportTypographyPresetFromClipboard(
      copiedPresets,
      presetId
    );

    if (foundCopiedPreset) {
      lodash.set(element.data.properties, path, {
        custom: foundCopiedPreset.custom
      });
    }
  });

  /*
   * Colors
   */
  const colorPaths = getPathsByType("color", builderElement);

  const maybeUpdateColor = (path: string, color: string) => {
    const foundCopiedColor = maybeImportColorFromClipboard(copiedColors, color);

    if (foundCopiedColor) {
      lodash.set(element.data.properties, path, foundCopiedColor.value);
    }
  };

  colorPaths.forEach(path => {
    const color = lodash.get(element.data.properties, path);

    if (!color) return;

    if (lodash.isObject(color)) {
      // Breakpoint values
      for (const property in color) {
        // @ts-ignore
        maybeUpdateColor(`${path}.${property}`, color[property]);
      }
    } else {
      maybeUpdateColor(path, color);
    }
  });

  return element;
}

export function importClipboardMetadataToElement(
  clipboardData: ClipboardData
): TreeNode {
  const recursiveImport = (element: TreeNode): TreeNode => {
    const newElement = moveExternalSiteGlobalsIntoElement(
      element,
      clipboardData.presets,
      clipboardData.colors
    );

    const children = element.children.map(recursiveImport);

    return {
      ...newElement,
      children
    };
  };

  return recursiveImport(clipboardData.element);
}

export function exportClipboardMetadataFromElement(
  element: TreeNode
): ClipboardData {
  // const recursiveExport = (
  //   element: TreeNode
  // ): { colors: GlobalColor[]; presets: TypographyPreset[] } => {
  //   const colors = extractGlobalColorsFromElement(element);
  //   const presets = extractTypographyPresetsFromElement(element);

  //   const children = element.children.map(recursiveExport).reduce(
  //     (a, b) => {
  //       return {
  //         colors: [...a.colors, ...b.colors],
  //         presets: [...a.presets, ...b.presets]
  //       };
  //     },
  //     { colors: [], presets: [] }
  //   );

  //   return {
  //     colors: [...colors, ...children.colors],
  //     presets: [...presets, ...children.presets]
  //   };
  // };

  const source = location.hostname;
  // const { colors, presets } = recursiveExport(element);

  return {
    source,
    element,
    colors: [], //arrayUniqueByKey<GlobalColor>(colors, "cssVariableName"),
    presets: [] //arrayUniqueByKey<TypographyPreset>(presets, "preset.id")
  };
}

// Typography

const maybeImportTypographyPresetFromClipboard = (
  copiedPresets: TypographyPreset[],
  presetIdToCopy: string
) => {
  if (isPresetInGlobalPresets(presetIdToCopy)) return;
  return copiedPresets.find(t => t.preset.id === presetIdToCopy);
};

export function extractTypographyPresetsFromElement(element: TreeNode) {
  const builderElement = configStore.element(element.data.type);

  return getPathsByType("typography", builderElement)
    .map(path => {
      return lodash.get(element.data.properties, `${path}.preset`);
    })
    .filter(path => !!path)
    .map(isPresetInGlobalPresets)
    .filter(isGlobalPreset);
}

function isPresetInGlobalPresets(presetId: string) {
  return globalStore.typographyPresets?.find(x => x.preset.id === presetId);
}

function isGlobalPreset(preset?: TypographyPreset): preset is TypographyPreset {
  return preset !== undefined;
}

// Colors

function maybeImportColorFromClipboard(
  copiedColors: GlobalColor[],
  color: string
) {
  if (!isCssVar(color) || (isCssVar(color) && isColorInGlobalColors(color))) {
    return;
  }

  const cssVar = extractVariableName(color);
  return copiedColors.find(c => c.cssVariableName === cssVar);
}

export function extractGlobalColorsFromElement(element: TreeNode) {
  const builderElement = configStore.element(element.data.type);

  return getPathsByType("color", builderElement)
    .map(path => lodash.get(element.data.properties, path))
    .filter(color => !!color)
    .map(maybeMapColorsObjectToArray)
    .flat()
    .filter(onlyUnique)
    .filter(isCssVar)
    .map(extractVariableName)
    .map(maybeGetGlobalColorByName)
    .filter(isGlobalColor);
}

function isCssVar(color: unknown) {
  return typeof color === "string" && color.startsWith("var(");
}

function isColorInGlobalColors(color: string) {
  const name = extractVariableName(color);
  return globalStore.globalColors.find(x => x.cssVariableName === name);
}

function maybeMapColorsObjectToArray(
  maybeObject: Record<string, string> | string
): string | string[] {
  return lodash.isObject(maybeObject)
    ? Object.values(maybeObject)
    : maybeObject;
}

function isGlobalColor(color?: GlobalColor): color is GlobalColor {
  return color !== undefined;
}

function maybeGetGlobalColorByName(color: string | null) {
  return globalStore.globalColors.find(x => x.cssVariableName === color);
}

function onlyUnique(value: unknown, index: number, self: unknown[]) {
  return self.indexOf(value) === index;
}

// function arrayUniqueByKey<T>(array: Array<T>, key: string) {
//   return [
//     ...new Map(array.map(item => [lodash.get(item, key), item])).values()
//   ];
// }
